import type { ReactNode } from "react";
import { Badge, Box, IconButton, Stack } from "@mui/material";
import { BusyButton } from "@gui/widgets/BusyButton";
import { BusyIcon } from "./BusyIcon";
import { PortalTooltip } from "@gui/widgets/PortalTooltip";
import type { PageAction } from "./PageLayout.types";
import { BusyAnnouncement } from "./BusyAnnouncement";

/** A page's commit group on a phone, wherever the page declared it (the top toolbar's right end, or
 *  the bottom toolbar). */
export interface PagePhoneActionsConfig {
	/** Secondary: Discard changes, Clear selection. */
	actions: readonly PageAction[];
	/** A control the primary needs (Skips' Skips Type). It takes the middle of the strip. */
	custom?: ReactNode;
	primaryAction?: PageAction;
}

const actionKey = (action: PageAction, index: number) => action.id ?? `${index}-${action.label}`;

/** The badge is hidden from assistive tech, so its count goes into the name instead. */
const accessibleName = (action: PageAction) => (action.badge ? `${action.label} (${action.badge})` : action.label);

/** 44px: a thumb-sized target. */
const TOUCH = 44;

const ICON_SX = {
	plain: { width: TOUCH, height: TOUCH, flex: "none" },
	primary: {
		width: 48,
		height: TOUCH,
		flex: "none",
		borderRadius: 1,
		bgcolor: "primary.main",
		color: "primary.contrastText",
		"&:hover": { bgcolor: "primary.dark" },
		"&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
	},
} as const;

function CountBadge(props: { action: PageAction; children: ReactNode }) {
	return (
		<Badge
			badgeContent={props.action.badge}
			color="error"
			invisible={!props.action.badge}
			slotProps={{ badge: { "aria-hidden": true } }}
		>
			{props.children}
		</Badge>
	);
}

/** An icon-only action, busy the BusyButton way: a spinner, `aria-busy`, and clicks ignored while it
 *  keeps focus (`aria-disabled`, not `disabled`). Its label is the tooltip and the accessible name. */
function IconAction(props: { action: PageAction; look: keyof typeof ICON_SX }) {
	const action = props.action;
	const busy = action.busy ?? false;
	return (
		<>
			<PortalTooltip title={action.label}>
				<IconButton
					aria-label={accessibleName(action)}
					onClick={busy ? undefined : action.onClick}
					disabled={!busy && action.disabled}
					aria-disabled={busy || undefined}
					aria-busy={busy || undefined}
					sx={ICON_SX[props.look]}
				>
					{busy ? <BusyIcon action={action} size={20} /> : action.icon}
				</IconButton>
			</PortalTooltip>
			<BusyAnnouncement action={action} />
		</>
	);
}

/**
 * A page's commit actions on a phone, in one row of the docked strip along the bottom of the page
 * (docs/design-system.md §4): the secondary actions as icon buttons at the left, then a `custom`
 * control filling the middle (or space), then the primary at the right. The primary is labelled,
 * unless a control shares the row, when it is an icon button. A `badge` sits on its corner.
 */
export function PagePhoneActions(props: { config: PagePhoneActionsConfig }) {
	const config = props.config;
	const primary = config.primaryAction;

	return (
		<Stack direction="row" spacing={1} useFlexGap alignItems="center">
			{config.actions.map((action, index) => (
				<IconAction key={actionKey(action, index)} action={action} look="plain" />
			))}
			<Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>{config.custom}</Box>
			{primary &&
				(config.custom ? (
					<CountBadge action={primary}>
						<IconAction action={primary} look="primary" />
					</CountBadge>
				) : (
					<CountBadge action={primary}>
						<BusyButton
							variant="contained"
							aria-label={primary.badge ? accessibleName(primary) : undefined}
							startIcon={primary.icon}
							onClick={primary.onClick}
							disabled={primary.disabled}
							busy={primary.busy}
							busyLabel={primary.busyLabel}
							sx={{ height: TOUCH }}
						>
							{primary.label}
						</BusyButton>
					</CountBadge>
				))}
		</Stack>
	);
}
